import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faKey, faRobot, faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import useGithubSshConfigs from '../hooks/useGithubSshConfigs';
import { useGitHubInstallations } from '../hooks/useGitHubInstallations';
import { useTenants } from '../hooks/useTenants';
import ResponsiveTable from './ResponsiveTable';
import GlossaryPanel from './GlossaryPanel';
import { useGlossary } from '../hooks/useGlossary';
import '../styles/shared.scss';

const ConnectionsOverview: React.FC = () => {
    const { data: sshConnections = [], isLoading: sshLoading } = useGithubSshConfigs();
    const { data: installations = [], isLoading: appLoading } = useGitHubInstallations();
    const { data: tenants = [] } = useTenants();
    const { isOpen, scrollToItem, openGlossary, closeGlossary } = useGlossary();

    const getTenantName = (tenantId: string) => {
        const tenant = tenants.find(t => t.id === tenantId);
        return tenant?.name || tenantId;
    };

    if (sshLoading || appLoading) {
        return <div className="loading-message">Loading connections...</div>;
    }

    return (
        <div>
            <div className="page-header">
                <div className="page-title-group">
                    <h2 className="page-title">Connections</h2>
                    <button
                        onClick={() => openGlossary('GitHub Connection')}
                        className="info-button"
                        title="View definition"
                    >
                        Info
                    </button>
                </div>
            </div>

            <div className="section">
                <div className="section-header">
                    <h3 className="section-title">
                        <FontAwesomeIcon icon={faKey} className="section-icon" />
                        GitHub SSH Connections
                    </h3>
                    <Link to="/configurations/connections/github" className="view-all-link">
                        View All
                    </Link>
                </div>
                
                {sshConnections.length === 0 ? (
                    <p className="empty-message">No SSH connections configured</p>
                ) : (
                    <ResponsiveTable
                        columns={[
                            {
                                key: 'name',
                                header: 'Name',
                                render: (value, row) => (
                                    <Link
                                        to="/configurations/connections/github"
                                        className="table-link"
                                    >
                                        {row.name || row.repositoryUrl.split('/').slice(-2).join('/')}
                                    </Link>
                                )
                            },
                            { 
                                key: 'repositoryUrl', 
                                header: 'Repository',
                                render: (value) => value.split('/').slice(-2).join('/')
                            },
                            { 
                                key: 'tenantId', 
                                header: 'Tenant',
                                render: (value) => getTenantName(value)
                            },
                            { 
                                key: 'keyType', 
                                header: 'Key Type',
                                render: (value) => (
                                    <span className="key-type-badge">
                                        {value?.toUpperCase() || 'SSH'}
                                    </span>
                                )
                            },
                            {
                                key: 'isActive',
                                header: 'Status',
                                render: (value) => (
                                    <span className={`status-badge ${value ? 'status-badge--active' : 'status-badge--inactive'}`}>
                                        <FontAwesomeIcon icon={value ? faCheck : faTimes} />
                                        {value ? 'Active' : 'Inactive'}
                                    </span>
                                )
                            }
                        ]}
                        data={sshConnections}
                    />
                )}
            </div>

            <div className="section">
                <div className="section-header">
                    <h3 className="section-title">
                        <FontAwesomeIcon icon={faRobot} className="section-icon" />
                        GitHub App Connections
                    </h3>
                    <Link to="/configurations/connections/github-app" className="view-all-link">
                        View All
                    </Link>
                </div>
                
                {installations.length === 0 ? (
                    <p className="empty-message">No GitHub App connections configured</p>
                ) : (
                    <ResponsiveTable
                        columns={[
                            {
                                key: 'name',
                                header: 'Name',
                                render: (value, row) => (
                                    <Link
                                        to="/configurations/connections/github-app"
                                        className="table-link"
                                    >
                                        {value}
                                    </Link>
                                )
                            },
                            { key: 'accountLogin', header: 'Account' },
                            { key: 'accountType', header: 'Type' },
                            { 
                                key: 'repositorySelection', 
                                header: 'Repository Selection',
                                render: (value) => value === 'all' ? 'All Repositories' : 'Selected'
                            },
                            {
                                key: 'isActive',
                                header: 'Status',
                                render: (value) => (
                                    <span className={`status-badge ${value ? 'status-badge--active' : 'status-badge--inactive'}`}>
                                        <FontAwesomeIcon icon={value ? faCheck : faTimes} />
                                        {value ? 'Active' : 'Inactive'}
                                    </span>
                                )
                            }
                        ]}
                        data={installations}
                    />
                )}
            </div>

            <GlossaryPanel 
                isOpen={isOpen} 
                onClose={closeGlossary} 
                scrollToItem={scrollToItem} 
            />
        </div>
    );
};

export default ConnectionsOverview;